'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface JobFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  locationFilter: string;
  onLocationChange: (value: string) => void;
  jobTypeFilter: string;
  onJobTypeChange: (value: string) => void;
  experienceFilter: string;
  onExperienceChange: (value: string) => void;
  salaryFilter: string;
  onSalaryChange: (value: string) => void;
}

const JobFilters = ({
  searchTerm,
  onSearchChange,
  locationFilter,
  onLocationChange,
  jobTypeFilter,
  onJobTypeChange,
  experienceFilter,
  onExperienceChange,
  salaryFilter,
  onSalaryChange,
}: JobFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);

  const locations = [
    'All Locations',
    'Dallas, TX',
    'Houston, TX',
    'Phoenix, AZ',
    'Denver, CO',
    'Atlanta, GA',
    'New York, NY',
    'Los Angeles, CA',
    'Chicago, IL',
    'Miami, FL'
  ];

  const jobTypes = [
    'All Types',
    'Full-time',
    'Part-time',
    'Contract',
    'Temporary',
    'Seasonal'
  ];

  const experienceLevels = [
    'All Levels',
    'Entry Level',
    'Mid Level',
    'Senior Level'
  ];

  const salaryRanges = [
    'All Ranges',
    '$30,000 - $45,000',
    '$45,000 - $60,000',
    '$60,000 - $75,000',
    '$75,000 - $90,000',
    '$90,000+'
  ];

  const quickSearchTerms = [
    'Heavy Equipment Operator',
    'Crane Operator',
    'Excavator Operator',
    'Forklift Operator',
    'Maintenance Technician',
    'Construction Manager',
    'Site Supervisor'
  ];

  const hasActiveFilters = locationFilter !== 'All Locations' || 
                          jobTypeFilter !== 'All Types' || 
                          experienceFilter !== 'All Levels' || 
                          salaryFilter !== 'All Ranges' ||
                          searchTerm !== '';

  const clearAllFilters = () => {
    onSearchChange('');
    onLocationChange('All Locations');
    onJobTypeChange('All Types');
    onExperienceChange('All Levels');
    onSalaryChange('All Ranges');
    setIsExpanded(false);
  };

  return (
    <div className="bg-card border border-border rounded-industrial industrial-shadow overflow-hidden">
      {/* Primary Search Bar */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-4">
          {/* Main Search Input */}
          <div className="flex-1 relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for jobs, companies, or skills..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => setShowQuickSearch(true)}
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-border rounded-industrial focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary bg-background transition-industrial"
              />
              <Icon 
                name="MagnifyingGlassIcon" 
                size={24} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primary" 
              />
              {searchTerm && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-industrial"
                >
                  <Icon name="XMarkIcon" size={16} className="text-text-secondary" />
                </button>
              )}
            </div>

            {/* Quick Search Suggestions */}
            {showQuickSearch && searchTerm === '' && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-industrial industrial-shadow z-50 max-h-64 overflow-y-auto">
                <div className="p-3 border-b border-border">
                  <p className="text-sm font-heading font-bold uppercase tracking-wide text-text-secondary">Popular Searches</p>
                </div>
                {quickSearchTerms.map((term, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      onSearchChange(term);
                      setShowQuickSearch(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-muted transition-industrial flex items-center space-x-3"
                  >
                    <Icon name="MagnifyingGlassIcon" size={16} className="text-text-secondary" />
                    <span>{term}</span>
                  </button>
                ))}
                <button
                  onClick={() => setShowQuickSearch(false)}
                  className="w-full px-4 py-2 text-center text-text-secondary hover:bg-muted transition-industrial border-t border-border"
                >
                  Close suggestions
                </button>
              </div>
            )}
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`px-6 py-4 border-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm transition-industrial flex items-center space-x-2 ${
              hasActiveFilters 
                ? 'border-primary bg-primary text-primary-foreground' 
                : 'border-border bg-background text-foreground hover:bg-muted'
            }`}
          >
            <Icon name="AdjustmentsHorizontalIcon" size={20} />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-primary-foreground text-primary rounded-full px-2 py-1 text-xs font-bold min-w-[20px] text-center">
                {[locationFilter !== 'All Locations', jobTypeFilter !== 'All Types', experienceFilter !== 'All Levels', salaryFilter !== 'All Ranges'].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {searchTerm && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-2 rounded-industrial text-sm">
                <span className="font-medium">"{searchTerm}"</span>
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-2 p-1 hover:bg-primary/20 rounded-full transition-industrial"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </div>
            )}
            {locationFilter !== 'All Locations' && (
              <div className="flex items-center bg-secondary/10 text-secondary px-3 py-2 rounded-industrial text-sm">
                <Icon name="MapPinIcon" size={14} className="mr-1" />
                <span className="font-medium">{locationFilter}</span>
                <button
                  onClick={() => onLocationChange('All Locations')}
                  className="ml-2 p-1 hover:bg-secondary/20 rounded-full transition-industrial"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </div>
            )}
            {jobTypeFilter !== 'All Types' && (
              <div className="flex items-center bg-accent/10 text-accent px-3 py-2 rounded-industrial text-sm">
                <Icon name="BriefcaseIcon" size={14} className="mr-1" />
                <span className="font-medium">{jobTypeFilter}</span>
                <button
                  onClick={() => onJobTypeChange('All Types')}
                  className="ml-2 p-1 hover:bg-accent/20 rounded-full transition-industrial"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </div>
            )}
            {experienceFilter !== 'All Levels' && (
              <div className="flex items-center bg-warning/10 text-warning px-3 py-2 rounded-industrial text-sm">
                <Icon name="AcademicCapIcon" size={14} className="mr-1" />
                <span className="font-medium">{experienceFilter}</span>
                <button
                  onClick={() => onExperienceChange('All Levels')}
                  className="ml-2 p-1 hover:bg-warning/20 rounded-full transition-industrial"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </div>
            )}
            {salaryFilter !== 'All Ranges' && (
              <div className="flex items-center bg-success/10 text-success px-3 py-2 rounded-industrial text-sm">
                <Icon name="CurrencyDollarIcon" size={14} className="mr-1" />
                <span className="font-medium">{salaryFilter}</span>
                <button
                  onClick={() => onSalaryChange('All Ranges')}
                  className="ml-2 p-1 hover:bg-success/20 rounded-full transition-industrial"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              </div>
            )}
            <button
              onClick={clearAllFilters}
              className="flex items-center bg-destructive/10 text-destructive px-3 py-2 rounded-industrial text-sm hover:bg-destructive/20 transition-industrial"
            >
              <Icon name="TrashIcon" size={14} className="mr-1" />
              <span className="font-medium">Clear All</span>
            </button>
          </div>
        )}
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="p-6 bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Location Filter */}
            <div>
              <label className="block font-heading text-sm font-bold uppercase tracking-wide mb-2 text-foreground">
                <Icon name="MapPinIcon" size={16} className="inline mr-1" />
                Location
              </label>
              <select
                value={locationFilter}
                onChange={(e) => onLocationChange(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-industrial focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              >
                {locations.map((location) => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            {/* Job Type Filter */}
            <div>
              <label className="block font-heading text-sm font-bold uppercase tracking-wide mb-2 text-foreground">
                <Icon name="BriefcaseIcon" size={16} className="inline mr-1" />
                Job Type
              </label>
              <select
                value={jobTypeFilter}
                onChange={(e) => onJobTypeChange(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-industrial focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              >
                {jobTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Experience Filter */}
            <div>
              <label className="block font-heading text-sm font-bold uppercase tracking-wide mb-2 text-foreground">
                <Icon name="AcademicCapIcon" size={16} className="inline mr-1" />
                Experience
              </label>
              <select
                value={experienceFilter}
                onChange={(e) => onExperienceChange(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-industrial focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              >
                {experienceLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Salary Filter */}
            <div>
              <label className="block font-heading text-sm font-bold uppercase tracking-wide mb-2 text-foreground">
                <Icon name="CurrencyDollarIcon" size={16} className="inline mr-1" />
                Salary
              </label>
              <select
                value={salaryFilter}
                onChange={(e) => onSalaryChange(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-industrial focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
              >
                {salaryRanges.map((range) => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close suggestions */}
      {showQuickSearch && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowQuickSearch(false)}
        />
      )}
    </div>
  );
};

export default JobFilters;